import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Wrench, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { parseSearch, normalizeText } from '@/lib/naturalSearch';
import { STATES, statePath } from '@/data/seo/states';
import { CITIES } from '@/data/seo/locations';
import { SEO_TRADES } from '@/data/seo/trades';
import { tradeLabel } from '@/data/constants';

interface Suggestion {
  key: string;
  label: string;
  to: string;
  kind: 'state' | 'city' | 'trade' | 'search';
}

/**
 * HOMEOWNER DIRECTORY SEARCH.
 *
 * Homeowners type the way they talk — "California", "TX", "Fontana",
 * "Roofing", "Electrician", "Painting", "Plumbing", "plumber in Corona" — and
 * get a short list of the right pages: the state page, the city page, the
 * trade page, plus live projects that match. Uses the same parser as the
 * marketplace and My Leads (src/lib/naturalSearch.ts), so every search box on
 * GIGGONE understands the same words.
 *
 * GIGGONE does not publish individual contractor profiles yet (see the
 * contractor-profile roadmap), so this routes to the location / trade pages
 * where homeowners post a project and matching contractors respond.
 */
const DirectorySearch: React.FC = () => {
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const suggestions = useMemo<Suggestion[]>(() => {
    const parsed = parseSearch(q);
    if (parsed.empty) return [];
    const out: Suggestion[] = [];
    const textWords = parsed.terms.filter((t) => t.kind === 'text').map((t) => t.text);
    const cityNeedle = textWords.join(' ');

    // Cities first when the user typed a place name ("Fontana", "San Bernardino").
    if (cityNeedle.length >= 3) {
      CITIES.filter((c) => normalizeText(c.name).startsWith(cityNeedle))
        .slice(0, 4)
        .forEach((c) => out.push({ key: `c-${c.slug}`, label: `${c.name}, CA`, to: `/california/${c.slug}`, kind: 'city' }));
    }

    parsed.terms.forEach((t) => {
      if (t.kind === 'state') {
        t.states.forEach((code) => {
          const st = STATES.find((s) => s.code === code);
          if (st) out.push({ key: `s-${code}`, label: es ? st.es : st.en, to: st.hub || statePath(st), kind: 'state' });
        });
      }
      if (t.kind === 'trade') {
        t.trades.slice(0, 4).forEach((slug) => {
          const seo = SEO_TRADES.find((x) => x.marketplaceTrade === slug || x.slug === slug);
          if (seo) out.push({ key: `t-${seo.slug}`, label: es ? seo.es : seo.en, to: `/trades/${seo.slug}`, kind: 'trade' });
          else out.push({ key: `t-${slug}`, label: tradeLabel(slug, lang), to: `/jobs?trade=${slug}`, kind: 'trade' });
        });
      }
    });

    // Always offer the live marketplace for the exact words typed.
    out.push({
      key: 'search',
      label: es ? `Ver proyectos abiertos: "${q.trim()}"` : `See open projects for "${q.trim()}"`,
      to: `/jobs?q=${encodeURIComponent(q.trim())}`,
      kind: 'search',
    });

    const seen = new Set<string>();
    return out.filter((s) => (seen.has(s.key) ? false : (seen.add(s.key), true))).slice(0, 8);
  }, [q, es, lang]);

  const go = (s?: Suggestion) => {
    if (!s) return;
    navigate(s.kind === 'search' ? `${path('/jobs')}?q=${encodeURIComponent(q.trim())}` : path(s.to));
  };

  const Icon = ({ kind }: { kind: Suggestion['kind'] }) =>
    kind === 'trade' ? <Wrench className="h-4 w-4 text-orange-500" /> : kind === 'search' ? <Search className="h-4 w-4 text-slate-400" /> : <MapPin className="h-4 w-4 text-orange-500" />;

  return (
    <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <h2 className="text-lg font-black text-slate-900">
        {es ? 'Encuentre contratistas por estado, ciudad u oficio' : 'Find contractors by state, city or trade'}
      </h2>
      <form
        role="search"
        className="relative mt-3"
        onSubmit={(e) => {
          e.preventDefault();
          go(suggestions[0]);
        }}
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={es ? 'California, TX, Fontana, plomero, electricista…' : 'California, TX, Fontana, roofing, electrician…'}
          aria-label={es ? 'Buscar contratistas' : 'Search contractors'}
          autoComplete="off"
          className="min-h-[48px] w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-800 focus:border-slate-900 focus:outline-none"
        />
      </form>
      {suggestions.length > 0 && (
        <ul className="mt-3 divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200">
          {suggestions.map((s) => (
            <li key={s.key}>
              <Link
                to={s.kind === 'search' ? `${path('/jobs')}?q=${encodeURIComponent(q.trim())}` : path(s.to)}
                className="flex min-h-[44px] items-center justify-between gap-3 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                <span className="flex items-center gap-2">
                  <Icon kind={s.kind} /> {s.label}
                </span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DirectorySearch;
